import { format, toZonedTime } from 'date-fns-tz';

/**
 * 日付をYYYY/MM/DD HH:mm:ss形式の文字列に変換する
 * @param date 日付オブジェクト
 * @returns フォーマットされた日付文字列 (YYYY/MM/DD HH:mm:ss)
 */
export function formatDateTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  // eslint-disable-next-line @stylistic/max-len
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * 日付オブジェクトを日本時間のISO日付文字列に変換する
 *
 * @param date 日付オブジェクト
 * @returns 日本時間のISO日付文字列
 */
export function toJpISOString(date: Date): string {
  return format(toZonedTime(date, 'Asia/Tokyo'), 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX');
}

/**
 * 日付をYYYYMMDD形式の文字列に変換する
 *
 * @param date 日付オブジェクト
 * @returns フォーマットされた日付文字列 (YYYYMMDD)
 */
export function formatDateWithoutSlash(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

/**
 * 日付をYYYY/MM/DD形式の文字列に変換する
 *
 * @param date 日付オブジェクト
 * @returns フォーマットされた日付文字列 (YYYY/MM/DD)
 */
export function formatDateWithSlash(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
}

/**
 * 日付を「D日 HH:mm」形式の文字列に変換する
 *
 * @param date 日付オブジェクト
 * @returns フォーマットされた日付文字列 (D日 HH:mm)
 */
export function formatDayAndTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getDate()}日 ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * yyyyMMddHHmm形式の文字列をDate型にパースする
 *
 * @param dateTimeString yyyyMMddHHmm形式の日付文字列
 * @returns パースされた日付オブジェクト
 * @throws 不正な形式の場合、Dateオブジェクトは無効な日付を返す
 */
export function parseCompactDateTime(dateTimeString: string): Date {
  if (dateTimeString.length !== 12) {
    return new Date(NaN); // Invalid Date
  }

  const year = Number.parseInt(dateTimeString.substring(0, 4), 10);
  const month = Number.parseInt(dateTimeString.substring(4, 6), 10) - 1; // 0-indexed
  const day = Number.parseInt(dateTimeString.substring(6, 8), 10);
  const hours = Number.parseInt(dateTimeString.substring(8, 10), 10);
  const minutes = Number.parseInt(dateTimeString.substring(10, 12), 10);

  return new Date(year, month, day, hours, minutes);
}

/**
 * yyyyMMddHHmmss形式の文字列をDate型にパースする
 *
 * @param dateTimeString yyyyMMddHHmmss形式の日付文字列
 * @returns パースされた日付オブジェクト
 * @throws 不正な形式の場合、Dateオブジェクトは無効な日付を返す
 */
export function parseFullDateTime(dateTimeString: string): Date {
  if (dateTimeString.length !== 14) {
    return new Date(NaN); // Invalid Date
  }

  const year = Number.parseInt(dateTimeString.substring(0, 4), 10);
  const month = Number.parseInt(dateTimeString.substring(4, 6), 10) - 1; // 0-indexed
  const day = Number.parseInt(dateTimeString.substring(6, 8), 10);
  const hours = Number.parseInt(dateTimeString.substring(8, 10), 10);
  const minutes = Number.parseInt(dateTimeString.substring(10, 12), 10);
  const seconds = Number.parseInt(dateTimeString.substring(12, 14), 10);

  return new Date(year, month, day, hours, minutes, seconds);
}

/**
 * yyyyMMdd形式の文字列をDate型にパースする
 *
 * @param dateString yyyyMMdd形式の日付文字列
 * @return パースされた日付オブジェクト
 * @throws 不正な形式の場合、Dateオブジェクトは無効な日付を返す
 */
export function parseDate(dateString: string): Date {
  if (dateString.length !== 8) {
    return new Date(NaN); // Invalid Date
  }

  const year = Number.parseInt(dateString.substring(0, 4), 10);
  const month = Number.parseInt(dateString.substring(4, 6), 10) - 1; // 0-indexed
  const day = Number.parseInt(dateString.substring(6, 8), 10);

  return new Date(year, month, day);
}
